import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { env } from "../../../env";
import axiosInstance from "../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const multiEntityAllMappings = createAsyncThunk(
  "multiEntityAllMappings",
  async (values, { rejectWithValue }) => {
    const { favourite, page_no, page_size, search_string, userId } = values;
    const key = `${page_no}_${page_size}_${favourite}`;

    try {
      const cachedRaw = sessionStorage.getItem("primaryEntityData");
      const cached = cachedRaw ? JSON.parse(cachedRaw) : null;

      if (cached && cached.hasOwnProperty(key)) {
        return cached[key];
      }

      const token = sessionStorage.getItem("q2p_token");
      if (!token) {
        return rejectWithValue("Missing authentication token");
      }

      const response = await axiosInstance.get(
        `${baseUrl}/sub_entity_mappings`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": token,
          },
          params: {
            page_no,
            page_size,
            search_string,
            sub_entity_mappings: { favourite },
          },
        },
      );

      const storedEntityDataRaw = sessionStorage.getItem("primaryEntityData");
      const storedEntityData = storedEntityDataRaw
        ? JSON.parse(storedEntityDataRaw)
        : {};

      const store = {
        ...response.data,
        mappings: response?.data?.mappings?.filter(
          (val) => val?.primary_user === userId,
        ),
      };
      if (!store.mappings?.length) {
        storedEntityData[key] = response?.data;
      } else {
        storedEntityData[key] = store;
      }

      sessionStorage.setItem(
        "primaryEntityData",
        JSON.stringify(storedEntityData),
      );

      return store;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";

      // CommonToast({
      //   text: message,
      //   icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      //   variant: "var(--error)",
      //   buttonNeeded: false,
      // });

      return rejectWithValue(message);
    }
  },
);

export const conciergeLogin = createAsyncThunk(
  "conciergeLogin",
  async (values, { rejectWithValue }) => {
    const { primary_entity_id, primary_user_id, sub_entity_id, sub_user_id } =
      values;

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/users/concierge_login`,
        {
          sessions: {
            primary_entity_id,
            primary_user_id,
            sub_entity_id,
            sub_user_id,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";

      // CommonToast({
      //   text: message,
      //   icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      //   variant: "var(--error)",
      //   buttonNeeded: false,
      // });
      return rejectWithValue(message);
    }
  },
);

export const addMultiEntityFavourite = createAsyncThunk(
  "multiEntity/addFavourite",
  async (values, { rejectWithValue }) => {
    const { entityId } = values;

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/sub_entity_mappings/${entityId}/add_favourite`,
        null,
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";

      // CommonToast({
      //   text: message,
      //   icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      //   variant: "var(--error)",
      //   buttonNeeded: false,
      // });
      return rejectWithValue(message);
    }
  },
);
export const removeMultiEntityFavourite = createAsyncThunk(
  "multiEntity/removeFavourite",
  async (values, { rejectWithValue }) => {
    const { entityId } = values;

    try {
      const response = await axiosInstance.put(
        `${baseUrl}/sub_entity_mappings/${entityId}/remove_favourite`,
        null,
        {
          headers: {
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );

      return response.data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.message ||
        "Something went wrong";

      // CommonToast({
      //   text: message,
      //   icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
      //   variant: "var(--error)",
      //   buttonNeeded: false,
      // });
      return rejectWithValue(message);
    }
  },
);

const multientitySlice = createSlice({
  name: "multientity",
  initialState: {
    multiEntityAllMappingsLoading: true,
    multiEntityAllMappingsData: null,
    multiEntityAllMappingsError: null,
    conciergeLoading: null,
    conciergeData: null,
    conciergeError: null,
    multiEntityFavouriteLoading: false,
    activeModules: [],
  },
  reducers: {
    resetMultiEntityAllMappingsData(state) {
      state.multiEntityAllMappingsData = null;
      state.multiEntityAllMappingsError = null;
    },
    activeModulesData(state, action) {
      state.activeModules = action.payload;
    },
    resetModulesData(state) {
      state.activeModules = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(multiEntityAllMappings.pending, (state) => {
      state.multiEntityAllMappingsLoading = true;
    });
    builder.addCase(multiEntityAllMappings.fulfilled, (state, action) => {
      state.multiEntityAllMappingsLoading = false;
      state.multiEntityAllMappingsData = action.payload;
    });
    builder.addCase(multiEntityAllMappings.rejected, (state) => {
      state.multiEntityAllMappingsError = true;
      state.multiEntityAllMappingsLoading = false;
      state.multiEntityAllMappingsData = null;
    });

    builder.addCase(conciergeLogin.pending, (state) => {
      state.conciergeLoading = true;
    });
    builder.addCase(conciergeLogin.fulfilled, (state, action) => {
      state.conciergeLoading = false;
      state.conciergeData = action.payload;
      sessionStorage.setItem("response", JSON.stringify(action.payload));
      sessionStorage.setItem("user_id", action.payload?.employees?.user_id);
      sessionStorage.setItem(
        "first_name",
        action.payload?.employees?.first_name,
      );
      sessionStorage.setItem("entity_id", action.payload?.entity?.entity_id);
      sessionStorage.setItem("loggedIn", true);
      sessionStorage.setItem("q2p_token", action.payload.q2p_token);
      sessionStorage.setItem("tokenGenerated", "false");
    });
    builder.addCase(conciergeLogin.rejected, (state) => {
      state.conciergeError = true;
      state.conciergeLoading = false;
      state.conciergeData = null;
    });
    builder.addCase(addMultiEntityFavourite.pending, (state) => {
      state.multiEntityFavouriteLoading = true;
    });
    builder.addCase(addMultiEntityFavourite.rejected, (state, action) => {
      state.multiEntityFavouriteLoading = false;
    });
    builder.addCase(addMultiEntityFavourite.fulfilled, (state, action) => {
      state.multiEntityFavouriteLoading = false;
    });
    builder.addCase(removeMultiEntityFavourite.pending, (state) => {
      state.multiEntityFavouriteLoading = true;
    });
    builder.addCase(removeMultiEntityFavourite.rejected, (state, action) => {
      state.multiEntityFavouriteLoading = false;
    });
    builder.addCase(removeMultiEntityFavourite.fulfilled, (state, action) => {
      state.multiEntityFavouriteLoading = false;
    });
  },
});
export const {
  resetMultiEntityAllMappingsData,
  activeModulesData,
  resetModulesData,
} = multientitySlice.actions;
export default multientitySlice.reducer;
